"use client";

// src/components/button/index.tsx
import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { button } from "@pinatagrams/design-system/recipes";
import { motion } from "motion/react";
import { useCallback } from "react";

// src/components/button/motion.ts
import { DURATION, OPACITY } from "@pinatagrams/design-system";
var variants = {
  bold: {
    disabled: {
      opacity: OPACITY.disabled
    },
    rest: {
      y: "0"
    },
    rise: {
      y: "-.25rem"
    },
    active: {
      y: "-.25rem",
      transition: {
        duration: DURATION.rapid
      }
    },
    focus: {
      y: "-.25rem",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  subtle: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  DEFAULT: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  ghost: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  loud: {
    disabled: {
      opacity: OPACITY.disabled
    },
    rest: {
      scaleX: "100%",
      scaleY: "100%"
    },
    rise: {
      scaleX: "110%",
      scaleY: "90%"
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  }
};

// src/components/button/sound/click.mp3
var click_default = "data:audio/mpeg;base64,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";

// src/components/button/sound/release.mp3
var release_default = "data:audio/mpeg;base64,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";

// src/components/button/index.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var MotionSlot = motion(Slot);
var Button = React.forwardRef(
  ({ emphasis, children, size = "md", icons, disabled, asChild, className, style, ...props }, ref) => {
    const { onBlur, onFocus, onMouseDown, onMouseUp } = props;
    const buttonClassName = button({ emphasis, size });
    const mergedClassName = className ? `${buttonClassName} ${className}` : buttonClassName;
    const buttonRef = React.useRef(null);
    const audioRef = React.useRef(null);
    const LeftIcon = icons?.[0];
    const RightIcon = icons?.[1];
    const setRefs = useCallback(
      (node) => {
        buttonRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );
    const audioButtonPress = useCallback((state) => {
      audioRef.current = new Audio(state === "click" ? click_default : release_default);
      const playPromise = audioRef.current.play();
      if (playPromise instanceof Promise) {
        playPromise.catch((error) => {
          console.error("Error playing audio:", error);
        });
      }
    }, []);
    const handleOnMouseDown = useCallback(
      (event) => {
        onMouseDown?.(event);
        audioButtonPress("click");
      },
      [onMouseDown, audioButtonPress]
    );
    const handleOnMouseUp = useCallback(
      (event) => {
        onMouseUp?.(event);
        audioButtonPress("release");
      },
      [onMouseUp, audioButtonPress]
    );
    const handleOnFocus = useCallback(
      (event) => {
        onFocus?.(event);
      },
      [onFocus]
    );
    const handleOnBlur = useCallback(
      (event) => {
        onBlur?.(event);
      },
      [onBlur]
    );
    const Component = asChild ? MotionSlot : motion.button;
    const content = asChild ? children : /* @__PURE__ */ jsxs(Fragment, { children: [
      LeftIcon && /* @__PURE__ */ jsx(LeftIcon, {}),
      children,
      RightIcon && /* @__PURE__ */ jsx(RightIcon, {})
    ] });
    return /* @__PURE__ */ jsx(
      Component,
      {
        ref: setRefs,
        className: mergedClassName,
        style,
        transition: {
          duration: 0.2,
          ease: "linear"
        },
        variants: variants[emphasis] || variants["DEFAULT"],
        initial: "rest",
        animate: disabled ? "disabled" : "rest",
        whileHover: "rise",
        whileTap: "active",
        whileFocus: "focus",
        layout: "position",
        onMouseDown: handleOnMouseDown,
        onMouseUp: handleOnMouseUp,
        onFocus: handleOnFocus,
        onBlur: handleOnBlur,
        disabled: asChild ? void 0 : disabled,
        ...props,
        children: content
      }
    );
  }
);
Button.displayName = "Button";
var button_default = Button;
export {
  Button,
  button_default as default
};
//# sourceMappingURL=button.js.map